/**
 * Utility-Funktionen für Theme-JavaScript
 */

/**
 * Debounce-Funktion zur Begrenzung von Event-Handler-Aufrufen
 * 
 * @param {Function} func - Auszuführende Funktion
 * @param {number} wait - Verzögerung in Millisekunden
 * @returns {Function} Debounced-Funktion
 */
export function debounce(func, wait = 100) {
	let timeout;
	return function(...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => {
			try {
				func.apply(this, args);
			} catch (error) {
				console.error('Fehler in debounced Funktion:', error);
			}
		}, wait);
	};
}

/**
 * Throttle-Funktion zur Begrenzung der Aufruffrequenz
 * 
 * @param {Function} func - Auszuführende Funktion
 * @param {number} limit - Minimum Zeit zwischen Aufrufen in Millisekunden
 * @returns {Function} Throttled-Funktion
 */
export function throttle(func, limit = 100) {
	let inThrottle;
	return function(...args) {
		if (!inThrottle) {
			try {
				func.apply(this, args);
			} catch (error) {
				console.error('Fehler in throttled Funktion:', error);
			}
			inThrottle = true;
			setTimeout(() => inThrottle = false, limit);
		}
	};
}

/**
 * Sichere Element-Selektion, die null zurückgibt, wenn kein Element gefunden
 * 
 * @param {string} selector - CSS-Selektor
 * @param {Element} [parent=document] - Parent-Element für die Suche
 * @returns {Element|null} Das gefundene Element oder null
 */
export function safeSelect(selector, parent = document) {
	if (!parent || !selector) return null;
	try {
		return parent.querySelector(selector);
	} catch (error) {
		console.error(`Fehler bei Selektion von "${selector}":`, error);
		return null;
	}
}

/**
 * Sichere Element-Selektion für mehrere Elemente, die leeres Array zurückgibt, wenn keine Elemente gefunden
 * 
 * @param {string} selector - CSS-Selektor
 * @param {Element} [parent=document] - Parent-Element für die Suche
 * @returns {Element[]} Array mit gefundenen Elementen oder leeres Array
 */
export function safeSelectAll(selector, parent = document) {
	if (!parent || !selector) return [];
	try {
		return Array.from(parent.querySelectorAll(selector));
	} catch (error) {
		console.error(`Fehler bei Mehrfachselektion von "${selector}":`, error);
		return [];
	}
}

/**
 * Anpassbare Announcement-Funktion für Screenreader
 * 
 * @param {string} message - Anzukündigende Nachricht
 * @param {string} [priority='polite'] - ARIA live Priorität ('polite' oder 'assertive')
 * @param {number} [clearDelay=1000] - Zeit in ms, bis Nachricht gelöscht wird
 */
export function announceForScreenReader(message, priority = 'polite', clearDelay = 1000) {
	try {
		// Prüfe bestehende Live-Region oder erstelle neue
		let liveRegion = document.querySelector(`[aria-live="${priority}"].sr-only`);
		
		if (!liveRegion) {
			liveRegion = document.createElement('div');
			liveRegion.setAttribute('aria-live', priority);
			liveRegion.classList.add('sr-only');
			document.body.appendChild(liveRegion);
		}
	
		// Text einfügen
		liveRegion.textContent = message;
		
		// Text nach angegebener Zeit löschen
		if (clearDelay > 0) {
			setTimeout(() => {
				liveRegion.textContent = '';
			}, clearDelay);
		}
	} catch (error) {
		console.error('Fehler beim Ankündigen für Screenreader:', error);
	}
}